import { mioIdeContextSchema, mioLearningModelSchema } from '@mio/shared';

import type { MioContext, MioIdeContext, MioLiveContext } from '@mio/shared';

export function createTabId() {
  // Do not reuse sessionStorage: duplicated tabs can inherit it and must remain distinct targets.
  return crypto.randomUUID();
}

/**
 * The Workshop page publishes only the identity of the attempt whose IDE is open, as data
 * attributes. The server resolves and authorizes it; no file or terminal content ever
 * passes through the browser's context.
 */
export function readIdeContext(activityId: string | null): MioIdeContext | null {
  const element = document.querySelector<HTMLElement>('[data-mio-ide-attempt]');
  if (!element || !activityId) return null;
  const parsed = mioIdeContextSchema.safeParse({
    kind: 'workshop',
    workspaceId: element.dataset.mioIdeWorkspace,
    attemptId: element.dataset.mioIdeAttempt,
    activityId: element.dataset.mioIdeActivity,
    generation: Number(element.dataset.mioIdeGeneration),
  });
  return parsed.success && parsed.data.activityId === activityId ? parsed.data : null;
}

/** Read only the published lesson surface. Never inspect a Workshop editor or iframe. */
export function captureLessonContext(
  page: MioContext,
  tabId: string,
  revision: number,
): MioLiveContext {
  const base: MioLiveContext = {
    ...page,
    tabId,
    revision,
    observedAt: new Date().toISOString(),
    ideContext: null,
  };
  if (page.environment === 'workshop') {
    const section = document.querySelector<HTMLElement>('[data-reading-section-id]');
    return {
      ...base,
      ideContext: readIdeContext(page.activity?.id ?? null),
      sectionId: section?.dataset.readingSectionId ?? null,
      sectionTitle: section?.dataset.readingSectionTitle ?? null,
    };
  }
  if (page.activity?.kind !== 'lesson') return base;
  const reader = document.querySelector<HTMLElement>('[data-lesson-id]');
  if (reader?.dataset.lessonId !== page.activity.id) return base;
  const pane = reader.closest<HTMLElement>('[data-scroll-pane]') ?? reader.parentElement;
  const viewport = pane?.getBoundingClientRect();
  const top = Math.max(0, viewport?.top ?? 0) + 40;
  const bottom = Math.min(window.innerHeight, viewport?.bottom ?? window.innerHeight);
  const blocks = [...reader.querySelectorAll<HTMLElement>('[data-learning-block]')];
  const visible = blocks.filter((block) => {
    const rect = block.getBoundingClientRect();
    return rect.bottom > top && rect.top < bottom;
  });
  const leadingBlock =
    visible.find((item) => item.getBoundingClientRect().top >= top) ?? visible[0];
  const modelBlock = visible.find((item) => item.querySelector('[data-learning-model]'));
  const traceBlock = visible.find((item) => item.querySelector('[data-trace-index]'));
  const selection = window.getSelection();
  const selected =
    selection?.anchorNode && reader.contains(selection.anchorNode)
      ? selection.toString().slice(0, 4000)
      : '';
  const selectedBlock =
    selected && selection?.anchorNode
      ? visible.find((item) => item.contains(selection.anchorNode))
      : undefined;
  // A step is meaningful only together with the block that owns its trace controls.
  const block = selectedBlock ?? modelBlock ?? traceBlock ?? leadingBlock;
  const headings = [
    ...reader.querySelectorAll<HTMLElement>(
      'h1[id][data-learning-block],h2[id][data-learning-block],h3[id][data-learning-block],h4[id][data-learning-block],h5[id][data-learning-block],h6[id][data-learning-block]',
    ),
  ];
  const heading = headings
    .filter(
      (item) => item.getBoundingClientRect().top <= (block?.getBoundingClientRect().top ?? top) + 1,
    )
    .at(-1);
  const trace =
    block === traceBlock ? traceBlock?.querySelector<HTMLElement>('[data-trace-index]') : null;
  return {
    ...base,
    activityId: page.activity.id,
    lessonVersion: reader.dataset.lessonVersion ?? null,
    sectionId: heading?.id ?? null,
    sectionTitle: heading?.textContent?.slice(0, 500) ?? null,
    blockId: block?.dataset.learningBlock ?? null,
    learningModel: modelBlock ? captureLearningModel(modelBlock) : null,
    traceStep: trace ? Number(trace.dataset.traceIndex ?? 0) : null,
    selection: selected,
    visibleText: visible
      .map((item) => item.textContent ?? '')
      .join('\n')
      .slice(0, 6000),
  };
}

export function contextFingerprint(context: MioLiveContext) {
  const { revision: _revision, observedAt: _time, ...stable } = context;
  return JSON.stringify(stable);
}

/** Only explicitly published teaching-model controls; never arbitrary DOM or IDE state. */
export function captureLearningModel(block: HTMLElement) {
  const model = block.querySelector<HTMLElement>('[data-learning-model]');
  if (!model) return null;
  try {
    const parsed = mioLearningModelSchema.safeParse({
      blockId: block.dataset.learningBlock,
      model: model.dataset.learningModel,
      state: JSON.parse(model.dataset.modelState ?? '{"inputs":{},"state":{}}') as unknown,
      actions: [...model.querySelectorAll<HTMLElement>('[data-model-action]')]
        .filter((control) => !control.closest('[hidden]'))
        .slice(0, 20)
        .map((control) => {
          const native =
            control instanceof HTMLInputElement ||
            control instanceof HTMLTextAreaElement ||
            control instanceof HTMLSelectElement;
          const kind =
            control.dataset.modelKind ??
            (control instanceof HTMLSelectElement
              ? 'select'
              : control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement
                ? 'input'
                : 'button');
          return {
            id: control.dataset.modelAction,
            label: (
              control.dataset.modelLabel ??
              control.getAttribute('aria-label') ??
              control.textContent ??
              ''
            )
              .trim()
              .slice(0, 200),
            kind,
            disabled: control.matches(':disabled,[aria-disabled="true"]'),
            ...(native || control.dataset.modelValue !== undefined
              ? {
                  value: (control instanceof HTMLInputElement && control.type === 'checkbox'
                    ? String(control.checked)
                    : native
                      ? control.value
                      : (control.dataset.modelValue ?? '')
                  ).slice(0, 500),
                }
              : {}),
            ...(control instanceof HTMLSelectElement
              ? {
                  options: [...control.options].slice(0, 20).map((option) => ({
                    value: option.value.slice(0, 500),
                    label: option.text.slice(0, 200),
                  })),
                }
              : control.dataset.modelOptions
                ? { options: JSON.parse(control.dataset.modelOptions) as unknown }
                : {}),
          };
        }),
    });
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
